import { Link, useNavigate } from "@tanstack/react-router";
import { CheckCheck, History, X } from "lucide-react";
import { Dialog } from "radix-ui";
import { useState } from "react";

import { Button } from "@/components/ui";
import { cn } from "@/lib/cn";

import { useAlertCenter } from "./center";
import { KindIcon } from "./AlertCard";
import { GROUPS, timeIST, type AlertItem } from "./inbox";

const SHOWN = 5; // per group before "Show N more"

const weekday = new Intl.DateTimeFormat("en-IN", {
  timeZone: "Asia/Kolkata",
  weekday: "short",
});
const sinceFmt = new Intl.DateTimeFormat("en-IN", {
  timeZone: "Asia/Kolkata",
  weekday: "short",
  day: "numeric",
  month: "short",
});

function when(value: string): string {
  const d = new Date(value);
  const today = new Date().toDateString() === d.toDateString();
  return `${today ? "Today" : weekday.format(d)} ${timeIST(d)}`;
}

function Row({ item, onOpen }: { item: AlertItem; onOpen: () => void }) {
  return (
    <li className="flex items-center gap-3 px-3.5 py-3">
      <KindIcon item={item} className="size-8 [&_svg]:size-4" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-fg">{item.title}</p>
        {item.body ? (
          <p className="truncate text-xs text-muted">{item.body}</p>
        ) : null}
      </div>
      <div className="shrink-0 text-right">
        <p className="text-2xs whitespace-nowrap text-subtle">
          {when(item.created_at)}
        </p>
        <button
          type="button"
          onClick={onOpen}
          className="text-xs font-semibold text-brand hover:underline"
        >
          Open
        </button>
      </div>
      <span
        aria-label="unread"
        className="size-2 shrink-0 rounded-full bg-brand"
      />
    </li>
  );
}

function Group({
  label,
  items,
  onOpen,
}: {
  label: string;
  items: AlertItem[];
  onOpen: (i: AlertItem) => void;
}) {
  const [all, setAll] = useState(false);
  const shown = all ? items : items.slice(0, SHOWN);
  return (
    <section>
      <h3 className="mb-2 flex items-center justify-between text-2xs font-semibold tracking-wide text-subtle uppercase">
        <span>{label}</span>
        <span className="num">{items.length}</span>
      </h3>
      <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border">
        {shown.map((i) => (
          <Row key={i.id} item={i} onOpen={() => onOpen(i)} />
        ))}
      </ul>
      {items.length > shown.length ? (
        <button
          type="button"
          onClick={() => setAll(true)}
          className="mt-1.5 text-xs font-semibold text-brand hover:underline"
        >
          Show {items.length - shown.length} more
        </button>
      ) : null}
    </section>
  );
}

/** "While you were away": every alert not seen yet, grouped by kind (none is skipped). */
export function AwayPopup() {
  const center = useAlertCenter();
  const navigate = useNavigate();
  const items = center.popup;
  if (!center.enabled || !items?.length) return null;
  const since = items.reduce((a, b) =>
    a.created_at < b.created_at ? a : b,
  ).created_at;
  const groups = GROUPS.map((g) => ({
    ...g,
    items: items.filter((i) => i.group === g.key),
  })).filter((g) => g.items.length);

  const close = () => center.closePopup();

  return (
    <Dialog.Root open onOpenChange={(o) => (o ? undefined : close())}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-[#0a1230]/45 backdrop-blur-[2px]" />
        <Dialog.Content
          // focus the dialog itself, not its first button: no ring on the close button
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            (e.currentTarget as HTMLElement | null)?.focus();
          }}
          tabIndex={-1}
          className={cn(
            "fixed z-50 flex flex-col border border-border bg-surface shadow-card outline-none",
            "inset-x-0 bottom-0 max-h-[92dvh] rounded-t-2xl",
            "sm:inset-auto sm:top-1/2 sm:left-1/2 sm:max-h-[86dvh] sm:w-[38rem] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-2xl",
          )}
        >
          <div className="flex items-start gap-3 px-5 pt-5 pb-3">
            <span
              aria-hidden
              className="grid size-10 shrink-0 place-items-center rounded-xl bg-brand-soft text-brand"
            >
              <History className="size-5" />
            </span>
            <div className="min-w-0 flex-1">
              <Dialog.Title className="font-display text-xl font-bold text-fg">
                While you were away
              </Dialog.Title>
              <Dialog.Description className="mt-0.5 text-sm text-muted">
                {items.length} alert{items.length === 1 ? "" : "s"} since{" "}
                {sinceFmt.format(new Date(since)).replace(",", "")},{" "}
                {timeIST(since)}
              </Dialog.Description>
            </div>
            <Dialog.Close
              aria-label="Close"
              className="rounded-md p-1 text-subtle hover:bg-surface-2 hover:text-fg"
            >
              <X className="size-5" />
            </Dialog.Close>
          </div>
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 pb-4">
            {groups.map((g) => (
              <Group
                key={g.key}
                label={g.label}
                items={g.items}
                onOpen={(i) => center.open(i)}
              />
            ))}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border bg-surface-2/60 px-5 py-3.5 pb-[max(0.875rem,env(safe-area-inset-bottom))] sm:rounded-b-2xl">
            {/* the popup per kind is the Popup column of Account → Notifications */}
            <Link
              to="/app/account"
              hash="notifications"
              onClick={() => close()}
              className="min-w-0 text-sm text-muted hover:text-fg hover:underline"
            >
              Change in Account
            </Link>
            <div className="ml-auto flex gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  close();
                  void navigate({ to: "/app/alerts" });
                }}
              >
                <History />
                Open history
              </Button>
              <Button
                onClick={() => {
                  center.read(items.map((i) => i.id));
                  close();
                }}
              >
                <CheckCheck />
                Mark all read
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
